import {
    type FC
} from "react";
import {
    View
} from "react-native";
import type ISheetHeaderProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useSheetContext
} from "../../context";
import {
    X as XIcon
} from "lucide-react-native";
import {
    getHitArea
} from "../../../../helpers/theme/control";
import {
    getModalCloseStyle
} from "../../../modal/stylesheet";
import AnimatedIcon from "../../../animatedIcon";
import Button from "../../../button";
import Text from "../../../text";

const SheetCloseIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    animation="signature"
    color={color}
    icon={XIcon}
    size={size}
/>;

const SheetHeader: FC<ISheetHeaderProps> = ({
    isShowClose = true,
    description,
    children,
    style,
    title,
    ...props
}) => {
    const sheet = useSheetContext({
        part: "Header"
    });

    const {
        controlHeights,
        touchTargets,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(sheet?.customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(sheet?.customLocalize);

    const pointerType = usePointerType();

    const closeStyle = getModalCloseStyle({
        controlHeights,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        radiuses,
        borders,
        spaces
    });

    const hitArea = getHitArea({
        visualSize: controlHeights.small,
        touchTargets,
        pointerType
    });

    const {
        container: containerDynamicStyle,
        close: closeDynamicStyle
    } = useStyles({
        isScrolled: sheet?.isScrolled ?? false,
        form: sheet?.form ?? "panel",
        hitSize: hitArea.minWidth,
        controlHeights,
        isShowClose,
        closeStyle,
        typography,
        iconSizes,
        borders,
        colors,
        spaces
    });

    if(!sheet) {
        return null;
    }

    const renderClose = () => {
        if(!isShowClose) {
            return null;
        }

        return <Button
            accessibilityLabel={localize("close")}
            borderRadius={closeStyle.borderRadius}
            customTheme={sheet.customTheme}
            onPress={sheet.requestClose}
            spreadBehaviour="free"
            style={[
                stylesheet.close,
                closeDynamicStyle
            ]}
            icon={SheetCloseIcon}
            variant="ghost"
            type="neutral"
            size="small"
        />;
    };

    const renderDescription = () => {
        if(!description) {
            return null;
        }

        return <Text
            customTheme={sheet.customTheme}
            id={sheet.descriptionId}
            variant="bodyMediumSize"
            color="mid"
        >
            {description}
        </Text>;
    };

    return <View
        {...props}
        style={[
            style,
            stylesheet.container,
            sheet.form === "panel" ? stylesheet.panelContainer : stylesheet.bottomContainer,
            containerDynamicStyle
        ]}
    >
        <Text
            customTheme={sheet.customTheme}
            variant="titleLargeSize"
            id={sheet.titleId}
            role="heading"
            color="high"
        >
            {title}
        </Text>
        {renderDescription()}
        {children}
        {renderClose()}
    </View>;
};
export default SheetHeader;
